import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getLeaders, getMeta } from "../lib/api";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
} from "recharts";

const CATS = [
  { key: "passing", label: "Passing", unit: "YDS", hero: "https://images.unsplash.com/photo-1640693316735-3fe5ee2de856" },
  { key: "rushing", label: "Rushing", unit: "YDS", hero: "https://images.pexels.com/photos/159515/football-american-football-runner-player-159515.jpeg" },
  { key: "receiving", label: "Receiving", unit: "YDS", hero: "https://images.pexels.com/photos/274577/pexels-photo-274577.jpeg" },
];

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-card border border-primary px-3 py-2 text-sm">
      <p className="font-semibold">{p.name}</p>
      <p className="font-display text-2xl text-primary leading-none">{p.yards} YDS</p>
    </div>
  );
};

export default function PlayerStats() {
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });
  const [cat, setCat] = useState("passing");
  const { data: leaders } = useQuery({ queryKey: ["leaders"], queryFn: getLeaders });

  const players = leaders?.[cat] || [];
  const active = CATS.find((c) => c.key === cat);
  const leader = players[0];
  const chartData = players.slice(0, 8).map((p) => ({ name: p.name.split(" ").slice(-1)[0], yards: p.stats.yards }));

  return (
    <div data-testid="stats-page" className="space-y-6">
      <div className="flex items-end justify-between border-b-2 border-border pb-2">
        <h1 className="font-display text-5xl">PLAYER STATS</h1>
        <span data-testid="stats-season-label" className="label-caps">{meta?.statsSeason} League Leaders</span>
      </div>

      <div data-testid="stat-category-tabs" className="flex border border-border">
        {CATS.map((c) => (
          <button
            key={c.key}
            data-testid={`cat-btn-${c.key}`}
            onClick={() => setCat(c.key)}
            className={`flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider transition-colors duration-150 ${
              cat === c.key ? "bg-primary text-black" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Leader spotlight */}
        {leader && (
          <div className="relative border border-border overflow-hidden min-h-[280px]">
            <img src={active.hero} alt={active.label} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/70" />
            <div className="relative p-6 h-full flex flex-col justify-end">
              <p className="label-caps text-primary">{active.label} Leader</p>
              <img src={leader.teamLogo} alt={leader.team} className="w-12 h-12 object-contain my-3" />
              <h3 className="font-display text-4xl leading-none">
                <Link to={`/player/${leader.playerId}`} data-testid="spotlight-player-link" className="hover:text-primary transition-colors duration-150">
                  {leader.name}
                </Link>
              </h3>
              <p className="text-muted-foreground text-sm mb-3">{leader.position} · {leader.teamName}</p>
              <p className="font-display text-6xl text-primary leading-none">{leader.stats.yards}</p>
              <p className="label-caps">{active.unit} · {leader.sub}</p>
            </div>
          </div>
        )}

        {/* Chart */}
        <div className="lg:col-span-2 border border-border bg-card p-5">
          <p className="label-caps mb-4">Top 8 · {active.label} Yards</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={chartData} margin={{ top: 0, right: 10, left: -10, bottom: 0 }}>
              <XAxis dataKey="name" stroke="hsl(0 0% 45%)" tick={{ fontSize: 11 }} />
              <YAxis stroke="hsl(0 0% 45%)" tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: "hsl(0 0% 100% / 0.05)" }} content={<ChartTooltip />} />
              <Bar dataKey="yards" radius={0}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill={i === 0 ? "hsl(75 100% 50%)" : "hsl(0 0% 30%)"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Full leaderboard */}
      <div data-testid="leaderboard-table" className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="label-caps border-b border-border">
              <th className="text-left font-normal px-4 py-3">#</th>
              <th className="text-left font-normal px-2 py-3">Player</th>
              <th className="text-left font-normal px-2 py-3 hidden sm:table-cell">Team</th>
              <th className="text-right font-normal px-2 py-3">Yards</th>
              <th className="text-right font-normal px-4 py-3">Detail</th>
            </tr>
          </thead>
          <tbody>
            {players.map((p, i) => (
              <tr
                key={p.id}
                data-testid={`leader-row-${p.id}`}
                className="border-b border-border/50 last:border-0 hover:bg-white/5 transition-colors duration-150"
              >
                <td className="px-4 py-3 font-display text-xl text-muted-foreground">{i + 1}</td>
                <td className="px-2 py-3 font-semibold">
                  <Link to={`/player/${p.playerId}`} data-testid={`player-link-${p.id}`} className="hover:text-primary transition-colors duration-150">
                    {p.name}
                  </Link>
                  <span className="ml-2 text-xs text-muted-foreground">{p.position}</span>
                </td>
                <td className="px-2 py-3 hidden sm:table-cell">
                  <Link to={`/team/${p.team}`} className="flex items-center gap-2 hover:text-primary transition-colors duration-150">
                    <img src={p.teamLogo} alt={p.team} className="w-5 h-5 object-contain" />
                    {p.team}
                  </Link>
                </td>
                <td className="px-2 py-3 text-right font-display text-2xl text-primary tabular-nums">{p.stats.yards}</td>
                <td className="px-4 py-3 text-right text-muted-foreground tabular-nums">{p.sub}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
